import type { Client } from '../../api/client.js';
import type { Tache } from './dependances.js';
import { FILTRES, LIBELLES_FILTRES, REGLES_FILE, REGLE_PAR_DEFAUT, type Filtre } from './donnees.js';

export type { Filtre } from './donnees.js';

export interface LigneFile {
  id: string;
  heure: string;
  titre: string;
  kind: string;
  badge: string | null;
  action: string;
  filtre: Filtre | 'autre';
  echeance: string | null;
  statut: string | null;
  dossierId: string | null;
  personId: string | null;
  version: number;
}

/** Une tâche du contrat dont le type peut être plus large que l'énumération actuelle (RFC-L52-01). */
export type TacheFile = Omit<Tache, 'kind'> & { kind: string };

const FUSEAU = 'America/Guadeloupe';
const formatHeure = new Intl.DateTimeFormat('fr-FR', { timeZone: FUSEAU, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
const formatDate = new Intl.DateTimeFormat('fr-FR', { timeZone: FUSEAU, day: '2-digit', month: '2-digit', year: 'numeric' });

export function construireFile(taches: readonly TacheFile[]): LigneFile[] {
  return taches
    .filter((t) => t.status === 'a_faire')
    .sort((a, b) => Date.parse(a.dueAt) - Date.parse(b.dueAt))
    .map((t) => {
      const regle = REGLES_FILE[t.kind] ?? REGLE_PAR_DEFAUT;
      return {
        id: t.id,
        heure: formatHeure.format(new Date(t.dueAt)),
        titre: t.title,
        kind: t.kind,
        badge: regle.badge,
        action: regle.action,
        filtre: regle.filtre,
        echeance: regle.afficherEcheance ? formatDate.format(new Date(t.dueAt)) : null,
        statut: regle.statut ?? null,
        dossierId: t.dossierId,
        personId: t.personId,
        version: t.rowVersion,
      };
    });
}

export function filtrer(lignes: readonly LigneFile[], filtre: Filtre): LigneFile[] {
  return filtre === 'tout' ? [...lignes] : lignes.filter((l) => l.filtre === filtre);
}

export function compter(lignes: readonly LigneFile[]): Record<Filtre, number> {
  return Object.fromEntries(FILTRES.map((f) => [f, filtrer(lignes, f).length])) as Record<Filtre, number>;
}

export const libelleFiltre = (filtre: Filtre, n: number): string => `${LIBELLES_FILTRES[filtre]} (${n})`;

export async function chargerFile(client: Client, jour: string): Promise<LigneFile[]> {
  const taches: TacheFile[] = [];
  let curseur: string | null = null;
  do {
    const requete: Record<string, string> = curseur ? { jour, cursor: curseur } : { jour };
    const page: { items: TacheFile[]; nextCursor: string | null } = await client.appeler('taches.fileDuJour', { requete });
    taches.push(...page.items);
    curseur = page.nextCursor;
  } while (curseur);
  return construireFile(taches);
}

/** Marque la tâche faite (PATCH avec If-Match). L'audit « tache.modification » est écrit par la route. */
export async function terminerTache(client: Client, ligne: LigneFile): Promise<void> {
  await client.appeler('taches.modifier', { parametres: { tacheId: ligne.id }, corps: { status: 'fait' }, versionLigne: ligne.version });
}

export const sansLigne = (lignes: readonly LigneFile[], id: string): LigneFile[] => lignes.filter((l) => l.id !== id);

/** Adresse de l'écran qui traite la tâche ; chaque écran s'ouvre sur le bon dossier (et la bonne personne). L'appel emporte aussi la tâche pour M02. */
export function adresseTache(ligne: LigneFile): string {
  const { destination } = REGLES_FILE[ligne.kind] ?? REGLE_PAR_DEFAUT;
  const q = new URLSearchParams();
  if (destination === '/appel') {
    q.set('tache', ligne.id);
    q.set('version', String(ligne.version));
  }
  if (ligne.personId) q.set('personne', ligne.personId);
  if (ligne.dossierId) q.set('dossier', ligne.dossierId);
  const requete = q.toString();
  return requete ? `${destination}?${requete}` : destination;
}
